/**
 * Autocomplete note titles inside [[...]] — makes wikilinking effortless.
 */
import { autocompletion, type CompletionContext, type CompletionResult } from "@codemirror/autocomplete";
import type { Extension } from "@codemirror/state";
import { ctxFor, getData } from "@/data/store";
import { editorSpace } from "./space";
import { slashSource } from "./slash-menu";

export function wikilinkSource(context: CompletionContext): CompletionResult | null {
  const before = context.matchBefore(/\[\[[^\][\n]*/);
  if (!before) return null;
  const query = before.text.slice(2).toLowerCase();

  // candidates: the note's own space first (a shared note links to its
  // folder's notes), then the personal workspace
  const space = context.state.facet(editorSpace);
  const pool = [];
  if (space?.shared) {
    const ctx = ctxFor(space.spaceId);
    if (ctx) pool.push(...ctx.workspace.getSnapshot());
  }
  pool.push(...getData().workspace.getSnapshot());

  const items = pool
    .filter((i) => i.type === "note" && !i.trashedAt && !i.archivedAt && i.title)
    .sort((a, b) => b.updatedAt - a.updatedAt);

  const mkApply =
    (insertText: string) =>
    (view: import("@codemirror/view").EditorView, _c: unknown, from: number, to: number) => {
      const after = view.state.sliceDoc(to, to + 2);
      const closing = after === "]]" ? "" : "]]";
      view.dispatch({
        changes: { from, to, insert: `${insertText}${closing}` },
        selection: { anchor: from + insertText.length + closing.length + (closing ? 0 : 2) },
      });
    };

  const options: import("@codemirror/autocomplete").Completion[] = [];
  for (const i of items) {
    if (!query || i.title.toLowerCase().includes(query)) {
      options.push({ label: i.title, type: "text", boost: i.pinned ? 1 : 0, apply: mkApply(i.title) });
    }
    for (const alias of i.aliases ?? []) {
      if (!query || alias.toLowerCase().includes(query)) {
        options.push({
          label: alias,
          detail: `→ ${i.title}`,
          type: "text",
          apply: mkApply(`${i.title}|${alias}`),
        });
      }
    }
  }
  options.sort((a, b) => (b.boost ?? 0) - (a.boost ?? 0));
  options.splice(30);

  if (options.length === 0) return null;
  return {
    from: before.from + 2,
    options,
    filter: false,
  };
}

export const hydraCompletions: Extension = autocompletion({
  override: [wikilinkSource, slashSource],
  icons: false,
  activateOnTyping: true,
});
